/**
 * Settings Dialog Store
 *
 * Zustand store for controlling the settings dialog programmatically.
 * Allows opening the dialog to a specific section from anywhere in the app.
 */

import { create } from 'zustand';
import type { SettingsSection } from '../components/settings/SettingsMenu';

/** Map section IDs to human-readable titles for analytics */
const sectionTitles: Record<SettingsSection, string> = {
  license: 'Account',
  connections: 'Connections',
  'azure-storage': 'Azure Storage',
  sessions: 'Sessions',
  snippets: 'Snippets',
  'meta-info': 'Meta Info',
  ui: 'UI',
  'keyboard-shortcuts': 'Keyboard Shortcuts',
  'ai-terminal': 'AI & Terminal',
  'ai-integrations': 'AI Integrations',
  updates: 'Updates',
  'whats-new': "What's New",
  privacy: 'Privacy',
  advanced: 'Advanced',
  spatial: 'Spatial',
  'backup-transfer': 'Backup & Transfer',
  'beta-features': 'Beta Features',
};

/** Track pageview for settings section */
const trackSettingsPageview = (section: SettingsSection) => {
  if (typeof window !== 'undefined' && window.electronAPI?.analytics?.pageview) {
    window.electronAPI.analytics
      .pageview(`/settings/${section}`, `Jam SQL - Settings: ${sectionTitles[section]}`)
      .catch(() => {});
  }
};

type SettingsDialogState = {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** The currently selected section */
  selectedSection: SettingsSection;

  // Actions
  /** Open the dialog to a specific section */
  openToSection: (section: SettingsSection) => void;
  /** Close the dialog */
  close: () => void;
  /** Set the selected section (when user navigates within dialog) */
  setSelectedSection: (section: SettingsSection) => void;
};

export const useSettingsDialogStore = create<SettingsDialogState>((set) => ({
  isOpen: false,
  selectedSection: 'license',

  openToSection: (section) => {
    trackSettingsPageview(section);
    set({ isOpen: true, selectedSection: section });
  },

  close: () => {
    set({ isOpen: false });
  },

  setSelectedSection: (section) => {
    trackSettingsPageview(section);
    set({ selectedSection: section });
  },
}));
